import { h, Fragment, Component } from 'preact';

import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Extracted verbatim from the inline IIFE that used to live at
// components/article/edit.js (the `activeTab === 'map'` branch), so the
// map-marker editor can be reused outside a saved article - e.g. the feed
// composer's `/kart` slash command.
//
// Decoupling: this component reads `mapMarkers`/`mapZoom`/`mapCenter` from
// props (never `article.id`) and reports every mutation via
// `onChange({ mapMarkers, mapZoom, mapCenter })` - the callback only
// receives the key(s) that actually changed. It never mutates the props it
// was given and never calls `articleStore.saveField` itself.
//
// Two-track design (see components/results/editor.js for the fuller
// writeup of why a single collapsed debounce silently drops edits):
// mapMarkers/mapZoom/mapCenter are mirrored into local state in the
// constructor. Render and every mutation work off that local state, never
// directly off props, so an in-flight (debounced) zoom/pan/label edit is
// never reverted by an unrelated re-render, and edits to different fields
// within the same debounce window both survive. Marker add/delete call
// `onChange` immediately (matching the original); zoom, pan and label
// rename debounce `onChange` by 750ms, computed from the freshly-updated
// local state at fire time - never from props.
//
// Leaflet's default marker icon (the `L.Icon.Default` URL patch) is NOT
// needed here: every <Marker> this component renders is given an explicit
// `icon` (a red-pill `L.divIcon`), so it never falls back to the default
// icon. The patch stays in article/edit.js, which still needs it for the
// separate `article.location` picker map that renders a plain, icon-less
// <Marker>.
function MapMarkerEvents({ onMapClick, onZoomEnd, onMoveEnd }) {
    useMapEvents({
        click(e) { onMapClick(e.latlng.lat, e.latlng.lng); },
        zoomend(e) { onZoomEnd(e.target.getZoom()); },
        moveend(e) {
            const center = e.target.getCenter();
            onMoveEnd(center.lat, center.lng);
        },
    });
    return null;
}

class MapEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            mapMarkers: props.mapMarkers || [],
            // A zoom of 0 is a real, legitimate value - only fall back to
            // the default of 13 when it's genuinely absent.
            mapZoom: Number.isFinite(props.mapZoom) ? props.mapZoom : 13,
            mapCenter: props.mapCenter || null,
            copiedMapTag: false,
        };
        // Tracks what we last handed to props.onChange per field, so
        // componentDidUpdate can tell "the parent echoed my own change
        // back" (skip - already reflected locally) from "something else
        // changed this prop externally" (adopt it).
        this.lastEmittedMarkers = props.mapMarkers;
        this.lastEmittedZoom = props.mapZoom;
        this.lastEmittedCenter = props.mapCenter;
    }

    componentDidUpdate(prevProps) {
        const updates = {};
        if (this.props.mapMarkers !== prevProps.mapMarkers
            && this.props.mapMarkers !== this.lastEmittedMarkers) {
            updates.mapMarkers = this.props.mapMarkers || [];
        }
        if (this.props.mapZoom !== prevProps.mapZoom
            && this.props.mapZoom !== this.lastEmittedZoom) {
            updates.mapZoom = Number.isFinite(this.props.mapZoom) ? this.props.mapZoom : 13;
        }
        if (this.props.mapCenter !== prevProps.mapCenter
            && this.props.mapCenter !== this.lastEmittedCenter) {
            updates.mapCenter = this.props.mapCenter || null;
        }
        if (Object.keys(updates).length > 0) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState(updates);
        }
    }

    handleMapClick = (lat, lng) => {
        const newMarker = {
            uuidv4: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            latitude: lat,
            longitude: lng,
            label: 'Ny markør',
        };
        const updated = [...this.state.mapMarkers, newMarker];
        this.lastEmittedMarkers = updated;
        this.setState({ mapMarkers: updated });
        this.props.onChange({ mapMarkers: updated });
    };

    handleZoomEnd = (z) => {
        this.setState({ mapZoom: z });
        if (this.zoomTimer) clearTimeout(this.zoomTimer);
        this.zoomTimer = setTimeout(() => {
            // Read the FRESH local state at fire time, never a snapshot
            // captured when the timer was scheduled.
            const zoomToEmit = this.state.mapZoom;
            this.lastEmittedZoom = zoomToEmit;
            this.props.onChange({ mapZoom: zoomToEmit });
        }, 750);
    };

    handleMoveEnd = (lat, lng) => {
        const newCenter = { latitude: lat, longitude: lng };
        this.setState({ mapCenter: newCenter });
        if (this.centerTimer) clearTimeout(this.centerTimer);
        this.centerTimer = setTimeout(() => {
            const centerToEmit = this.state.mapCenter;
            this.lastEmittedCenter = centerToEmit;
            this.props.onChange({ mapCenter: centerToEmit });
        }, 750);
    };

    updateMarkerLabel = (uuidv4, label) => {
        const updated = this.state.mapMarkers.map(mk =>
            (mk.uuidv4 === uuidv4 ? { ...mk, label } : mk));
        this.setState({ mapMarkers: updated });

        const timerKey = `marker-${uuidv4}`;
        if (!this.markerLabelTimers) this.markerLabelTimers = {};
        if (this.markerLabelTimers[timerKey]) clearTimeout(this.markerLabelTimers[timerKey]);
        this.markerLabelTimers[timerKey] = setTimeout(() => {
            const markersToEmit = this.state.mapMarkers;
            this.lastEmittedMarkers = markersToEmit;
            this.props.onChange({ mapMarkers: markersToEmit });
        }, 750);
    };

    deleteMarker = (uuidv4) => {
        const updated = this.state.mapMarkers.filter(mk => mk.uuidv4 !== uuidv4);
        this.lastEmittedMarkers = updated;
        this.setState({ mapMarkers: updated });
        this.props.onChange({ mapMarkers: updated });
    };

    render() {
        const { mapMarkers: markers, mapZoom: zoom, mapCenter: center, copiedMapTag } = this.state;
        // A latitude/longitude of 0 is a real, legitimate coordinate - only
        // fall back to the default (Norway-ish) centre when there's
        // genuinely no centre set yet.
        const hasCenter = center != null
            && Number.isFinite(center.latitude)
            && Number.isFinite(center.longitude);
        const mapLat = hasCenter ? center.latitude : 62;
        const mapLng = hasCenter ? center.longitude : 10;

        return (
            <div>
                <small class='text-body-secondary d-block mb-2'>
                    Klikk på kartet for å legge til markører. Lim inn <code>{'{{map}}'}</code> i brødteksten for å bestemme hvor kartet vises.
                </small>
                <button
                    type='button'
                    class='btn btn-sm btn-outline-secondary rounded-pill px-2 py-0 mb-2'
                    onClick={() => {
                        navigator.clipboard.writeText('{{map}}');
                        this.setState({ copiedMapTag: true });
                        setTimeout(() => this.setState({ copiedMapTag: false }), 2000);
                    }}
                >
                    <small>
                        {copiedMapTag
                            ? <><i class='fa-solid fa-check me-1' />Kopiert</>
                            : <><i class='fa-solid fa-copy me-1' />{'{{map}}'}</>
                        }
                    </small>
                </button>

                <div style='height: 50vh; border-radius: 10px; overflow: hidden;'>
                    <MapContainer
                        center={[mapLat, mapLng]}
                        zoom={hasCenter ? zoom : 5}
                        scrollWheelZoom={true}
                        style={{ height: '100%', width: '100%' }}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://statkart.no">Kartverket</a>'
                            url='https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png'
                        />
                        <MapMarkerEvents
                            onMapClick={this.handleMapClick}
                            onZoomEnd={this.handleZoomEnd}
                            onMoveEnd={this.handleMoveEnd}
                        />
                        {markers.map(m => {
                            const icon = L.divIcon({
                                html: `<div style="background: #dc3545; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px; white-space: nowrap; font-weight: bold; box-shadow: 0 1px 4px rgba(0,0,0,0.3);">${m.label || ''}</div>`,
                                className: 'map-marker-label',
                                iconSize: null,
                                iconAnchor: [12, 12],
                            });
                            return (
                                <Marker
                                    key={m.uuidv4}
                                    position={[m.latitude, m.longitude]}
                                    icon={icon}
                                />
                            );
                        })}
                    </MapContainer>
                </div>

                {markers.length > 0 && (
                    <div class='mt-3'>
                        <small class='text-body-secondary d-block mb-1'>Markører ({markers.length}):</small>
                        <div class='d-flex flex-column gap-2'>
                            {markers.map((m, idx) => (
                                <div key={m.uuidv4} class='d-flex align-items-center gap-2'>
                                    <span class='badge text-bg-secondary'>{idx + 1}</span>
                                    <input
                                        type='text'
                                        class='form-control form-control-sm'
                                        value={m.label || ''}
                                        placeholder='Markørnavn...'
                                        onInput={e => this.updateMarkerLabel(m.uuidv4, e.target.value)}
                                    />
                                    <small class='text-body-secondary text-nowrap'>
                                        {parseFloat(m.latitude).toFixed(4)}, {parseFloat(m.longitude).toFixed(4)}
                                    </small>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-danger rounded-pill px-2 py-0'
                                        onClick={() => this.deleteMarker(m.uuidv4)}
                                    >
                                        <i class='fa-solid fa-trash' />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default MapEditor;
